import React from "react";
import { useTranslation } from "react-i18next";
import { format } from "date-fns";
import { Theme } from "@mui/material";
import {
  Event,
  toCurrency,
  toCurrencyWithoutLargeCents,
  toRoundedNumber,
  toString,
} from "@helpers";
import { ComparisonValue, SecuritiesChartSelectedType } from "@components";
import { CustomTooltipContainer } from "../components/CustomTooltipContainer";
import { Item } from "./Item";
import { EventItem } from "./EventItem";

const toGermanFormatter = (value: number = 0, currency: string) =>
  toCurrency(value, currency);
const toStringFormatter = (value: number = 0) => toString(value, undefined, 2);
const toPercentFormatter = (value: number = 0) => `${toRoundedNumber(value)} %`;

interface Props {
  dateTime: number;
  comparedTicker: ComparisonValue | undefined;
  chartType: "performance" | "valuation";
  currency?: string;
  theme: Theme;
  events: Event[];
  mainValueLabel?: string;
  // Phones: smaller type and box, and large amounts without cents.
  compact?: boolean;
  points: {
    value: number;
    color: string;
    name: SecuritiesChartSelectedType | "comparison";
    displayName?: string;
  }[];
}

export const CustomTooltip = ({
  dateTime,
  points,
  events,
  comparedTicker,
  mainValueLabel,
  currency,
  chartType,
  theme,
  compact = false,
}: Props) => {
  const { t } = useTranslation();
  if (!points.length) return null;

  const subtitleMap = {
    all: format(dateTime, "d.M.yyyy"),
    comparison: comparedTicker?.label,
    holdings: t("securitiesChart.legend.holdings"),
    funds: t("securitiesChart.legend.funds"),
    etfs: t("securitiesChart.legend.etfs"),
    derivative: t("securitiesChart.legend.derivative"),
    bonds: t("securitiesChart.legend.bonds"),
  };

  const formatter =
    chartType === "valuation"
      ? currency
        ? compact
          ? toCurrencyWithoutLargeCents
          : toGermanFormatter
        : toStringFormatter
      : toPercentFormatter;

  const allPoint = points.find((point) => point.name === "all");
  const otherPoints = points.filter((point) => point.name !== "all");

  return (
    <CustomTooltipContainer
      theme={theme}
      compact={compact}
      firstRow={
        allPoint ? (
          <Item
            theme={theme}
            isStrong
            compact={compact}
            color={allPoint.color}
            title={formatter(allPoint.value, currency ?? "")}
            subtitle={
              <div>
                <p
                  style={{
                    margin: theme.spacing(0, 0, 1),
                  }}
                >
                  {subtitleMap.all}
                </p>
                {mainValueLabel && (
                  <p
                    style={{
                      margin: 0,
                    }}
                  >
                    {mainValueLabel}
                  </p>
                )}
              </div>
            }
          />
        ) : null
      }
    >
      {otherPoints?.map((point, index) => {
        if (point.name === "all") return null;
        return (
          <Item
            key={`item-${index}`}
            theme={theme}
            compact={compact}
            color={point.color}
            title={formatter(point.value, currency ?? "")}
            subtitle={
              point.displayName ??
              subtitleMap[point.name as keyof typeof subtitleMap]
            }
          />
        );
      })}
      {events?.map((event, index) => (
        <EventItem key={`event-${index}`} event={event} theme={theme} />
      ))}
    </CustomTooltipContainer>
  );
};
